﻿import { DateTime } from 'luxon';
window.DateTime = DateTime;
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { make, replaceMe, parseOptions } from '../components/utils';

// Declare antiforgeryRequestToken as a global variable (provided by server-side rendering)
declare const antiforgeryRequestToken: string;

// Define a minimal type for Tabulator columns
type TabulatorColumn = {
  authorized?: boolean;
  [key: string]: any;
};

type TabulatorOptions = {
  columns: TabulatorColumn[];
  ajaxConfig?: {
    headers?: Record<string, string>;
    [key: string]: any;
  };
  [key: string]: any;
};


function customButton(
  value: string,
  formatterParams: {
    url: string;
    css?: string | string[];
    text?: string;
    isBlank?: boolean;
  }
): string {
  const href = replaceMe(formatterParams.url, value);
  const link = make('a', formatterParams.css, {
    href: href,
    innerHTML: formatterParams.text || ''
  });
  if (formatterParams.isBlank) {
    link.target = '_blank';
  }
  return link.outerHTML;
}

Tabulator.extendModule('format', 'formatters', {
  deletedForm: function (cell: any, formatterParams: any) {
    let form = make('form', ['flex', 'gap-1'], {
      method: 'POST',
      action: formatterParams.url,
    });

    let input = make('input', [], {
      type: 'hidden',
      value: cell.getValue(),
      name: cell.getField()
    });
    let token = make('input', [], {
      type: 'hidden',
      value: antiforgeryRequestToken,
      name: "__RequestVerificationToken",
    });
    let restoreBtn = make('button', ['btn', 'btn-xs', 'btn-square', 'btn-outline', 'btn-success'], {
      name: 'deletedAction',
      type: 'submit',
      value: 'restore',
      innerHTML: '<i class="icon-[mdi--delete-restore]" title="Ripristina"></i>'
    });
    let deleteBtn = make('button', ['btn', 'btn-xs', 'btn-square', 'btn-outline', 'btn-error'], {
      name: 'deletedAction',
      type: 'submit',
      value: 'harddelete',
      innerHTML: '<i class="icon-[mdi--delete]" title="Elimina Definitivamente"></i>'
    });

    form.appendChild(input);
    form.appendChild(restoreBtn);
    form.appendChild(deleteBtn);
    form.appendChild(token);

    return form.outerHTML;
  },
  'custom-button': function (cell: any, formatterParams: any) {
    let defaultParams = {
      resizable: false
    };

    return customButton(cell.getValue(), { ...defaultParams, ...formatterParams });
  },
  'edit-button': function (cell: any, formatterParams: any) {
    const defaultParams = {
      text: '<i class="icon-[mdi--pencil-box]" title="Modifica"></i>',
      css: 'btn btn-xs btn-square btn-outline btn-info'
    }
    let value = cell.getValue();
    return customButton(value, { ...defaultParams, ...formatterParams });
  },
  'delete-button': function (cell: any, formatterParams: any) {
    let defaultParams = {
      text: '<i class="icon-[mdi--delete]" title="Elimina"></i>',
      css: 'btn btn-xs btn-square btn-outline btn-error'
    }
    let value = cell.getValue();
    return customButton(value, { ...defaultParams, ...formatterParams });
  },
  'details-button': function (cell: any, formatterParams: any) {
    let defaultParams = {
      text: '<i class="icon-[mdi--eye-circle]" title="Dettagli"></i>',
      css: 'btn btn-xs btn-square btn-outline btn-warning'
    }
    let value = cell.getValue();
    return customButton(value, { ...defaultParams, ...formatterParams });
  },
  'report-button': function (cell: any, formatterParams: any) {
    let defaultParams = {
      text: '<i class="icon-[mdi--file-document]" title="Report"></i>',
      css: 'btn btn-xs btn-square btn-outline btn-info'
    }
    let value = cell.getValue();
    return customButton(value, { ...defaultParams, ...formatterParams });
  }
});

export const tabulator = {
  init: function (selector: string | HTMLElement, options: TabulatorOptions): Tabulator {
    // If selector is an element and contains data-tabulator, parse and merge those options
    let mergedOptions: TabulatorOptions = { ...(tabulator.defaultOptions as any) };

    if (selector instanceof HTMLElement) {
      const raw = selector.dataset?.tabulator ?? null;
      if (raw) {
        const parsed = parseOptions<Partial<TabulatorOptions>>(raw, {}) as Partial<TabulatorOptions>;
        mergedOptions = { ...(mergedOptions as any), ...(parsed as any) };
      }
    }

    // Merge explicit options passed to the init call (override element/data and defaults)
    mergedOptions = { ...(mergedOptions as any), ...(options as any) };

    // Filter out unauthorized columns if provided
    if (Array.isArray(mergedOptions.columns)) {
      mergedOptions.columns = mergedOptions.columns
        .filter((column: TabulatorColumn) => column.authorized === undefined || column.authorized === true)
        .map(({ authorized, ...rest }) => rest);
    }

    // Ensure antiforgery token header is present when ajaxConfig exists
    if (mergedOptions.ajaxConfig && mergedOptions.ajaxConfig.headers) {
      mergedOptions.ajaxConfig.headers['RequestVerificationToken'] = antiforgeryRequestToken;
    }

    return new Tabulator(selector, mergedOptions);
  },
  defaultOptions: {
    locale: 'it',
    langs: {
      'it': {
        'data': {
          'loading': 'Caricamento...',
          'error': 'Errore',
        },
        'groups': {
          'item': 'elemento',
          'items': 'elementi',
        },
        'pagination': {
          'page_size': 'Visualizza',
          'page_title': 'Elementi per pagina',
          'first': '«',
          'first_title': 'Prima pagina',
          'last': '»',
          'last_title': 'Ultima pagina',
          'prev': '‹',
          'prev_title': 'Pagina precedente',
          'next': '›',
          'next_title': 'Pagina successiva',
          'all': 'All',
          'counter': {
            'showing': 'Visualizza',
            'of': 'di',
            'rows': 'elementi',
            'pages': 'pagine',
          }
        },
        'headerFilters': {
          'default': 'Filtra...'
        }
      }
    },
    columnDefaults: {
      vertAlign: 'middle',
      resizable: 'header',
    },
    resizableRows: false,
    selectableRows: false,
    renderVertical: 'basic',
    layout: 'fitColumns',
    responsiveLayout: 'hide',
    ajaxConfig: {
      method: 'POST',
      headers: {}
    },
    ajaxResponse: function (url: any, params: any, response: any) {
      return {
        page: response.pageNumber,
        last_row: response.totalCount,
        last_page: response.totalPages,
        data: response.items
      };
    },
    filterMode: 'remote',
    sortMode: 'remote',
    pagination: true,
    paginationMode: 'remote',
    paginationSize: 20,
    paginationSizeSelector: [5, 10, 20, 50],
    paginationCounter: 'rows',
    placeholder: 'Nessun elemento disponibile',
    placeholderHeaderFilter: 'Nessun elemento disponibile in base ai filtri impostati',
    persistence: {
      sort: true,
      filter: false,
      headerFilter: true,
      group: false,
      page: true,
      columns: false,
    }
  }
};

